import type { GlobalToken } from 'antd';
import { useMemo } from 'react';
import type { ITheme } from '../../../styles/theme';
import { getTypographyLinkStyles } from './use-get-typography-link-styles';

export enum TypographyVariant {
  H1 = 'h1',
  H2 = 'h2',
  H3 = 'h3',
  H4 = 'h4',
  LARGE_BODY_BOLD = 'largeBodyBold',
  LARGE_BODY_REGULAR = 'largeBodyRegular',
  MEDIUM_BODY_BOLD = 'mediumBodyBold',
  MEDIUM_BODY_REGULAR = 'mediumBodyRegular',
  MEDIUM_BODY_ITALIC = 'mediumBodyItalic',
  LINKS_SEMIBOLD = 'linksSemibold',
  LINKS_MEDIUM = 'linksMedium',
  LINKS_SMALL = 'linksSmall',
  SMALL_BODY_BOLD = 'smallBodyBold',
  SMALL_BODY_REGULAR = 'smallBodyRegular',
  LARGE_EYEBROW = 'largeEyebrow',
  SMALL_EYEBROW = 'smallEyebrow',
  DISCLAIMER = 'disclaimer',
  DATA_TABLE_VALUES = 'dataTableValues',
  CALENDAR_EVENT_CONTENTS = 'calendarEventContents',
  OVERLINE = 'overline',
}

type TypographyStyles = {
  fontFamily?: string;
  fontWeight: string;
  fontSize: string;
  letterSpacing: string;
  lineHeight: string;
  color: GlobalToken['colorWhite'];
  fontStyle?: string;
  textTransform?: 'uppercase';
  textDecorationLine?: string;
};

export const getTypographyStyles = (
  currentVariant: TypographyVariant,
  theme: ITheme,
  themeToken: GlobalToken,
  color?: GlobalToken['colorWhite'],
) => {
  const { colors } = theme;
  const linkStyles = getTypographyLinkStyles(themeToken, false, color);
  const styles: Record<TypographyVariant, TypographyStyles> = {
    h1: {
      fontWeight: '700',
      fontSize: '60px',
      letterSpacing: '0%',
      lineHeight: '64px',
      color: colors.secondary,
    },
    h2: {
      fontWeight: '700',
      fontSize: '40px',
      letterSpacing: '0%',
      lineHeight: '44px',
      color: colors.secondary,
    },
    h3: {
      fontWeight: '700',
      fontSize: '30px',
      letterSpacing: '0%',
      lineHeight: '32px',
      color: colors.secondary,
    },
    h4: {
      fontWeight: '400',
      fontSize: '30px',
      letterSpacing: '0%',
      lineHeight: '36px',
      color: colors.secondary,
    },
    largeBodyBold: {
      fontWeight: '700',
      fontSize: '18px',
      letterSpacing: '0%',
      lineHeight: '28px',
      color: colors.secondary,
    },
    largeBodyRegular: {
      fontWeight: '400',
      fontSize: '18px',
      letterSpacing: '0%',
      lineHeight: '28px',
      color: colors.secondary,
    },
    mediumBodyBold: {
      fontWeight: '700',
      fontSize: '16px',
      letterSpacing: '0%',
      lineHeight: '24px',
      color: colors.secondary,
    },
    mediumBodyRegular: {
      fontWeight: '400',
      fontSize: '16px',
      letterSpacing: '0%',
      lineHeight: '24px',
      color: colors.secondary,
    },
    mediumBodyItalic: {
      fontStyle: 'italic',
      fontWeight: '400',
      fontSize: '16px',
      letterSpacing: '0%',
      lineHeight: '24px',
      color: colors.secondary,
    },
    linksSemibold: {
      fontWeight: '600',
      fontSize: '16px',
      letterSpacing: '0%',
      lineHeight: '24px',
      ...linkStyles,
    },
    // Should match the text styles for FLAT buttons
    linksMedium: {
      fontWeight: '400',
      fontSize: '16px',
      letterSpacing: '0%',
      lineHeight: '24px',
      ...linkStyles,
    },
    linksSmall: {
      fontWeight: '500',
      fontSize: '14px',
      letterSpacing: '0%',
      lineHeight: '20px',
      ...linkStyles,
    },
    smallBodyBold: {
      fontWeight: '700',
      fontSize: '14px',
      letterSpacing: '0%',
      lineHeight: '20px',
      color: colors.secondary,
    },
    smallBodyRegular: {
      fontWeight: '400',
      fontSize: '14px',
      letterSpacing: '0%',
      lineHeight: '20px',
      color: colors.secondary,
    },
    largeEyebrow: {
      fontWeight: '700',
      fontSize: '14px',
      letterSpacing: '3px',
      lineHeight: '20px',
      color: colors.primary,
      textTransform: 'uppercase',
    },
    smallEyebrow: {
      fontWeight: '700',
      fontSize: '12px',
      letterSpacing: '3px',
      lineHeight: '12px',
      color: colors.primary,
      textTransform: 'uppercase',
    },
    disclaimer: {
      fontWeight: '400',
      fontSize: '12px',
      letterSpacing: '0%',
      lineHeight: '16px',
      color: colors.secondary,
    },
    dataTableValues: {
      fontFamily: '"Inconsolata", sans-serif',
      fontWeight: '600',
      fontSize: '16px',
      letterSpacing: '0%',
      lineHeight: '24px',
      color: colors.secondary,
    },
    calendarEventContents: {
      fontWeight: '500',
      fontSize: '14px',
      lineHeight: '17.5px',
      letterSpacing: '0%',
      color: colors.secondary,
      textTransform: 'uppercase',
    },
    overline: {
      fontWeight: '600',
      fontSize: '20px',
      lineHeight: '24px',
      letterSpacing: '1',
      color: colors.secondary,
      textTransform: 'uppercase',
    },
  };

  const typographyClasses = {
    fontFamily: '"Barlow", sans-serif',
    ...styles[currentVariant],
    margin: 0,
    color: color || styles[currentVariant].color,
  };

  return typographyClasses;
};

export const useGetTypographyStyles = (
  currentVariant: TypographyVariant,
  theme: ITheme,
  themeToken: GlobalToken,
  color?: GlobalToken['colorWhite'],
) => {
  const styles = useMemo(
    () => getTypographyStyles(currentVariant, theme, themeToken, color),
    [currentVariant, theme, themeToken, color],
  );
  return styles;
};
